/**
 * Shared helpers for reading and writing URL search params without navigation.
 * Used by analytics pages to persist filters and tabs in the query string.
 */

export const getSearchParam = (key: string): string | null =>
  new URLSearchParams(window.location.search).get(key)

export const getCommaSeparatedSearchParam = (key: string): string[] => {
  const raw = getSearchParam(key)
  return raw ? raw.split(',') : []
}

export const setSearchParam = (key: string, value: string): void => {
  const url = new URL(window.location.href)
  if (!value) {
    url.searchParams.delete(key)
  } else {
    url.searchParams.set(key, value)
  }
  window.history.replaceState(null, '', url)
}

/** Persist the active tab while leaving all other query params intact. */
export const setTabSearchParam = (tab: string): void => {
  setSearchParam('tab', tab)
}

/**
 * Build a route path that carries over the current query string.
 * Params listed in `exclude` are dropped from both the current location and
 * any query string already on `path`. Current-location params are merged on
 * top of target params (current wins on duplicate keys). Hash fragments on
 * `path` are preserved.
 */
export const buildRouteWithPreservedSearch = (
  path: string,
  exclude: string[] = []
): string => {
  const target = new URL(path, 'http://placeholder')
  const currentParams = new URLSearchParams(window.location.search)
  const targetParams = new URLSearchParams(target.search)

  for (const key of exclude) {
    currentParams.delete(key)
    targetParams.delete(key)
  }

  for (const [key, value] of currentParams) {
    targetParams.set(key, value)
  }

  const search = targetParams.toString()
  return `${target.pathname}${search ? `?${search}` : ''}${target.hash}`
}
